import { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Container, Typography, Grid, TextField } from "@mui/material";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import LocalizationProvider from "@mui/lab/LocalizationProvider";
import DatePicker from "@mui/lab/DatePicker";
import { withSnackbar } from "notistack";
import { Box, Button, Checkbox } from "@mui/material";
import { useNewPatientContext } from "../store/NewPatientContextProvider";
import { createStudy, getProcedures, getUsersByRole } from "../api/api";
import { useHistory } from "react-router-dom";
import { useAuthUser } from "../store/AuthUserContextProvider";
import Autocomplete from "@mui/material/Autocomplete";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { CircleSpinner } from "react-spinners-kit";
import { useDataLists } from "../store/DataListsContextProvider";
import { v4 as uuidv4 } from "uuid";
import { setPatientToInit } from "../store/action-reducers";
import { TimePicker } from "@mui/lab";

function CreateStudy(props) {
  const history = useHistory();
  const { lists } = useDataLists();
  const { authUser } = useAuthUser();
  const { newPatient, newPatientDispatch } = useNewPatientContext();
  const [isSaving, setSaving] = useState(false);
  const [remoteStudyRouteData, setRemoteStudyRouteData] = useState({
    checked: false,
    users: {
      radiographers: [],
      radiologists: [],
      receptionists: [],
      refDoctors: [],
    },
    procedures: [],
  });
  const [chosenProcedures, setChosenProcedures] = useState([]);
  const [form, setForm] = useState({
    SessionID: newPatient.PatientDetails.SessionID,
    MedSchemeAuthNumber: newPatient.PatientDetails.MedSchemeAuthNumber,
    FirstName: newPatient.PatientDetails.FirstName,
    LastName: newPatient.PatientDetails.LastName,
    Title: newPatient.PatientDetails.Title,
    Schedule: null,
    Receptionist: authUser.FirstName
      ? `${authUser.FirstName} ${authUser.LastName}`
      : "",
    Radiographer: "NONE",
    Radiologist: "NONE",
    RefDoctor: remoteStudyRouteData.users.refDoctors.length
      ? `${remoteStudyRouteData.users.refDoctors[0].FirstName} ${remoteStudyRouteData.users.refDoctors[0].LastName}`
      : "",
    SecondaryRefDoctor: remoteStudyRouteData.users.refDoctors.length
      ? `${remoteStudyRouteData.users.refDoctors[0].FirstName} ${remoteStudyRouteData.users.refDoctors[0].LastName}`
      : "",
    Procedures: [],
    Branch: newPatient.PatientDetails.Branch,
    IDNumber: newPatient.PatientDetails.IDNumber,
    Sex: newPatient.PatientDetails.Sex,
    MedicalAidNumber: newPatient.MedicalAidDetails.MedicalAidNumber,
  });
  const [date, setDate] = useState(null);

  useEffect(() => {
    if (!newPatient.PatientDetails.IDNumber) {
      history.push("/patient");
      return;
    }

    const getPageRemoteData = async () => {
      const [
        radiographer_res,
        radiologist_res,
        receptionist_res,
        ref_doctor_res,
        procedures_res,
      ] = await Promise.all([
        getUsersByRole("radiographer"),
        getUsersByRole("radiologist"),
        getUsersByRole("receptionist"),
        getUsersByRole("ref-doctor"),
        getProcedures(),
      ]);
      const [radiographer_data, radiographer_err] = radiographer_res;
      const [radiologist_data, radiologist_err] = radiologist_res;
      const [receptionist_data, receptionist_err] = receptionist_res;
      const [ref_doctor_data, ref_doctor_err] = ref_doctor_res;
      const [procedures_data, procedures_err] = procedures_res;

      if (radiographer_err) {
        props.enqueueSnackbar(
          `ERROR FETCHING RADIOGRAPHER LIST : ${radiographer_err.message}`,
          {
            preventDuplicate: true,
            variant: "error",
          }
        );
        return;
      } else if (radiologist_err) {
        props.enqueueSnackbar(
          `ERROR FETCHING RADIOLOGISTS LIST : ${radiologist_err.message}`,
          {
            preventDuplicate: true,
            variant: "error",
          }
        );
        return;
      } else if (receptionist_err) {
        props.enqueueSnackbar(
          `ERROR FETCHING RECEPTIONIST LIST : ${receptionist_err.message}`,
          {
            preventDuplicate: true,
            variant: "error",
          }
        );
        return;
      } else if (ref_doctor_err) {
        props.enqueueSnackbar(
          `ERROR FETCHING REF-DOCTOR LIST : ${ref_doctor_err.message}`,
          {
            preventDuplicate: true,
            variant: "error",
          }
        );
        return;
      } else if (procedures_err) {
        props.enqueueSnackbar(
          `ERROR FETCHING PROCEDURE LIST : ${procedures_err.message}`,
          {
            preventDuplicate: true,
            variant: "error",
          }
        );
        return;
      }

      const payload = {
        users: {
          radiographers: radiographer_data.data,
          radiologists: radiologist_data.data,
          receptionists: receptionist_data.data,
          refDoctors: ref_doctor_data.data,
        },
        procedures: procedures_data.data,
        checked: true,
      };

      setRemoteStudyRouteData(payload);

      setForm({
        ...form,
        Receptionist: payload.users.receptionists[0]
          ? payload.users.receptionists[0].FirstName +
            " " +
            payload.users.receptionists[0].LastName
          : "",
        Radiographer: "NONE",
        Radiologist: "NONE",
        RefDoctor: payload.users.refDoctors[0]
          ? payload.users.refDoctors[0].UserName : "",
        SecondaryRefDoctor: payload.users.refDoctors[0]
          ? payload.users.refDoctors[0].UserName : "",
      });
    };

    getPageRemoteData();
  }, []);

  useEffect(() => {

    setForm({
      ...form,
      Schedule: new Date(date).getTime(),
      Timestamp: new Date().getTime(),
    });
  }, [date]);

  useEffect(() => {
    const procedures = lists.procedures.filter(
      (proc) => form.Procedures.indexOf(proc.Name) >= 0
    );
    setChosenProcedures(procedures);
  }, [form.Procedures]);

  const handleInputChange = (e) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    });
  };

  const handleCheckboxChange = (selections, isProcedureCode) => {
    if (selections[0] !== undefined) {
      if (isProcedureCode) {
        let chosenProcedures = lists.procedures.filter(
          (proc) => selections.indexOf(proc.Prefix) >= 0
        );

        setForm({
          ...form,
          Room:chosenProcedures.map((proc) => proc.Room),
          RoomCode:chosenProcedures.map((proc) => proc.RoomCode),
          Procedures: chosenProcedures.map((proc) => proc.Name),
          Examination: chosenProcedures.map((proc) =>{
            console.log("proc.Procedures",proc.Procedures);
             return proc.Procedures
            }),
        });
      } else {
        setForm({
          ...form,
          Room:chosenProcedures.map((proc) => proc.Room),
          RoomCode:chosenProcedures.map((proc) => proc.RoomCode),
          Procedures: selections.map((procedure) => procedure),
          Examination: chosenProcedures.map((proc) =>{
            console.log("proc.Procedures",proc.Procedures);
             return proc.Procedures
            }),
        });
      }
    }
  };

  const handleSubmit = async () => {
    setSaving(true);

    if (form.Procedures.length === 0) {
      props.enqueueSnackbar("PLEASE SELECT A STUDY", {
        preventDuplicate: true,
        variant: "error",
      });
      setSaving(false);
      return;
    }

    if (!form.Schedule) {
      props.enqueueSnackbar("PLEASE SELECT A SCHEDULE DATE", {
        preventDuplicate: true,
        variant: "error",
      });
      setSaving(false);
      return;
    }

    if ([...Object.values(form)].findIndex((val) => val === "") >= 0) {
      {
        props.enqueueSnackbar("PLEASE FILL IN ALL INPUTS", {
          preventDuplicate: true,
          variant: "error",
        });
        setSaving(false);
        return;
      }
    }

    try {
      const [data, err] = await createStudy(form);

      if (err) {
        props.enqueueSnackbar(err.message, {
          preventDuplicate: true,
          variant: "error",
        });
        setSaving(false);
      } else {
        props.enqueueSnackbar("STUDY CREATED SUCCESSFULLY", {
          preventDuplicate: true,
          variant: "success",
        });
        setSaving(false);
        history.push("worklist");
      }
    } catch (err) {
      props.enqueueSnackbar(err.message, {
        preventDuplicate: true,
        variant: "error",
      });
      setSaving(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Create Study</title>
      </Helmet>

      <Container style={{ padding: 20 }} maxWidth="md">
        <Typography
          variant="h5"
          gutterBottom
          sx={{ mb: 3, color: "#fff", opacity: 0.8 }}
          component="div"
        >
          Patient
        </Typography>

        
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <TextField
              label="First Name"
              variant="outlined"
              name="FirstName"
              disabled={true}
              value={form.FirstName}
              fullWidth
              onChange={handleInputChange}
            />
          </Grid>

          <Grid item xs={4}>
            <TextField
              label="Last Name"
              variant="outlined"
              name="LastName"
              disabled={true}
              value={form.LastName}
              onChange={handleInputChange}
              fullWidth
            />
          </Grid>

          <Grid item xs={4}>
            <TextField
              label="Title"
              variant="outlined"
              name="Title"
              disabled={true}
              value={form.Title}
              onChange={handleInputChange}
              fullWidth
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="Session ID"
              variant="outlined"
              name="SessionID"
              disabled={true}
              value={form.SessionID}
              onChange={handleInputChange}
              fullWidth
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="MedSchemeAuthNumber"
              variant="outlined"
              name="MedSchemeAuthNumber"
              value={form.MedSchemeAuthNumber}
              fullWidth
              onChange={handleInputChange}
            />
          </Grid>
          {Boolean(lists.branches.length) && (
        <Grid
          item
          xs={4}
          sx={{
            display: "flex",
            justifyContent: "center",
          }}
        >
          <TextField
            select
            label="Branch"
            name="Branch"
            id="Branch"
            onChange={handleInputChange}
            value={form.Branch}
            SelectProps={{
              native: true,
            }}
            sx={{ maxWidth: "240px", minWidth: "240px" }}
            fullWidth
          >
            {lists.branches.map((branch) => {
              return (
                <option value={branch.Prefix} key={uuidv4()}>
                  {branch.Name}
                </option>
              );
            })}
          </TextField>
        </Grid>
      )}
          <Grid item xs={4}>
            <TextField
              select
              label="pregnant"
              variant="outlined"
              name="pregnant"
              value={!form.pregnant  ? "N": form.pregnant}
              fullWidth
              onChange={handleInputChange}
              SelectProps={{
                native: true,
              }}
            >
              
            <option defaultChecked key="No" value="N">
              No
            </option>

               <option key="yes" value="Y">
              Yes
            </option>
            </TextField>
          </Grid>

          <Grid item xs={6}>
             <TextField
                  id="datetime-local"
                  label="Schedule"
                  type="datetime-local"
                  defaultValue={date}
                  onChange={(newValue) => {
                    //////'
                    console.log("newValue",newValue)
                    console.log("newVtarget.valuealue",newValue.target.value)
                    setDate(newValue.target.value);
                  }}
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
          </Grid>

          {!remoteStudyRouteData.checked && (
            <Grid item md={12}>
              <Box
                sx={{
                  p: 4,
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <CircleSpinner />
              </Box>
            </Grid>
          )}

          {Boolean(remoteStudyRouteData.users.receptionists.length) && (
            <Grid item xs={4}>
              <TextField
                select
                label="Receptionist Name"
                name="Receptionist"
                onChange={handleInputChange}
                value={form.Receptionist}
                SelectProps={{
                  native: true,
                }}
                fullWidth
              >
                {remoteStudyRouteData.users.receptionists.map(
                  (receptionist, index) => {
                    return (
                      <option
                        key={"option-" + index}
                        value={receptionist.UserName}
                      >
                        {receptionist.UserName}
                      </option>
                    );
                  }
                )}
              </TextField>
            </Grid>
          )}

          {Boolean(remoteStudyRouteData.users.radiographers.length) && (
            <Grid item xs={4}>
              <TextField
                select
                label="Radiographer Name"
                name="Radiographer"
                onChange={handleInputChange}
                value={form.Radiographer}
                SelectProps={{
                  native: true,
                }}
                fullWidth
              >
                <option value={`NONE`}>NONE</option>
                {remoteStudyRouteData.users.radiographers.map(
                  (radiographer, index) => {
                    return (
                      <option
                        key={"option-" + index}
                        value={radiographer.UserName}
                      >
                        {radiographer.FirstName}.{radiographer.LastName}
                      </option>
                    );
                  }
                )}
              </TextField>
            </Grid>
          )}

          {Boolean(remoteStudyRouteData.users.radiologists.length) && (
            <Grid item xs={4}>
              <TextField
                select
                label="Radiologist Name"
                name="Radiologist"
                value={form.Radiologist}
                onChange={handleInputChange}
                SelectProps={{
                  native: true,
                }}
                fullWidth
              >
                <option value={`NONE`}>NONE</option>
                {remoteStudyRouteData.users.radiologists.map(
                  (radiologist, index) => {
                    return (
                      <option
                        key={"option-" + index}
                        value={radiologist.UserName}
                      >
                        {radiologist.FirstName}.{radiologist.LastName}
                      </option>
                    );
                  }
                )}
              </TextField>
            </Grid>
          )}

          {Boolean(remoteStudyRouteData.users.refDoctors.length) && (
            <Grid item xs={6}>
              <TextField
                select
                label="Ref doctor"
                name="RefDoctor"
                onChange={handleInputChange}
                value={form.RefDoctor}
                SelectProps={{
                  native: true,
                }}
                fullWidth
              >
                {remoteStudyRouteData.users.refDoctors.map((doctor, index) => {
                  return (
                    <option key={"option-" + index} value={doctor.UserName}>
                    DR {doctor.FirstName}.{doctor.LastName}
                    </option>
                  );
                })}
              </TextField>
            </Grid>
          )}

          {Boolean(remoteStudyRouteData.users.refDoctors.length) && (
            <Grid item xs={6}>
              <TextField
                select
                label="Secondary Ref doctor"
                name="SecondaryRefDoctor"
                onChange={handleInputChange}
                value={form.SecondaryRefDoctor}
                SelectProps={{
                  native: true,
                }}
                fullWidth
              >
                {remoteStudyRouteData.users.refDoctors.map((doctor, index) => {
                  return (
                    <option key={"option-" + index} value={doctor.UserName}>
                      DR {doctor.FirstName}.{doctor.LastName}
                    </option>
                  );
                })}
              </TextField>
            </Grid>
          )}
        </Grid>

        <br />

        <Typography
          variant="h5"
          gutterBottom
          sx={{ mb: 3, color: "#fff", opacity: 0.8 }}
          component="div"
        >
          Procedures
        </Typography>

        <Grid container spacing={1}>
          <Grid item xs={6}>
            <StudiesAutocomplete
              options={lists.procedures.map((procedure) => procedure.Prefix)}
              handleCheckboxChange={handleCheckboxChange}
              isProcedureCode={true}
              value={chosenProcedures.map((proc) => proc.Prefix)}
            />
          </Grid>

          <Grid item xs={6}>
            <StudiesAutocomplete
              options={lists.procedures.map((procedure) => procedure.Name)}
              handleCheckboxChange={handleCheckboxChange}
              value={chosenProcedures.map((proc) => proc.Name)}
            />
          </Grid>

          <Grid item xs={12}>
            <Box
              sx={{
                height: "100%",
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "flex-end",
              }}
            >
              <Button
                color="primary"
                variant="outlined"
                size="large"
                onClick={() => {
                  newPatientDispatch(setPatientToInit());
                  history.push("/");
                }}
                disabled={isSaving}
                sx={{ mr: 2 }}
              >
                Cancel
              </Button>

              <Button
                color="secondary"
                variant="contained"
                size="large"
                onClick={handleSubmit}
                disabled={isSaving}
              >
                {isSaving ? "Creating..." : "Create"}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </>
  );
}

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

function StudiesAutocomplete(props) {
  return (
    <>
      {props.options.length ? (
        <Autocomplete
          multiple
          id="studies-selection-input-field"
          options={props.options}
          value={props.value}
          disableCloseOnSelect
          onChange={(e, selections) => {
            props.handleCheckboxChange(selections, props.isProcedureCode);
          }}
          getOptionLabel={(option) => option}
          renderOption={(props, option, { selected }) => (
            <li {...props} key={uuidv4()}>
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                style={{ marginRight: 8 }}
                checked={selected}
              />
              {option}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label={
                props.isProcedureCode
                  ? "Select Procedure by code"
                  : "Select Procedure by name"
              }
              placeholder="select procedures from list"
            />
          )}
        />
      ) : (
        <div />
      )}
    </>
  );
}

export default withSnackbar(CreateStudy);

